import { useState, useEffect, useCallback } from "react";
import {
  MenuItem,
  Select,
  Avatar,
  Button,
  CircularProgress,
  Box,
  Chip,
} from "@mui/material";
import { Send, Refresh } from "@mui/icons-material";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import {
  getOrganizations,
  sendInvoiceAction,
  updateInvoiceStatus,
} from "../../Actions/OrganizationAction";
import { listTripolaTransactionDataAction } from "../../Actions/TripolaPlanActions";
import { listKycTransactionDataAction } from "../../Actions/KycPlanManagemantAction";
import DataTable from "../../components/DataTable/DataTable";

const NAVY = "#22419e";

const avatarSx = {
  width: 34,
  height: 34,
  bgcolor: "#eef2ff",
  color: NAVY,
  fontSize: 13,
  fontWeight: 700,
  borderRadius: "10px",
};

const moneySx = {
  fontSize: 15,
  fontWeight: 600,
  color: "#333",
  fontVariantNumeric: "tabular-nums",
};

const chipBaseSx = {
  height: 26,
  fontWeight: 600,
  fontSize: 11.5,
  borderRadius: "8px",
  "& .MuiChip-label": { px: 1.2 },
};

const statusChipSx = {
  success: {
    ...chipBaseSx,
    bgcolor: "#ecfdf5",
    color: "#047857",
    border: "1px solid rgba(4, 120, 87, 0.2)",
  },
  warning: {
    ...chipBaseSx,
    bgcolor: "#fff7ed",
    color: "#c2410c",
    border: "1px solid rgba(194, 65, 12, 0.2)",
  },
  error: {
    ...chipBaseSx,
    bgcolor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid rgba(185, 28, 28, 0.2)",
  },
  info: {
    ...chipBaseSx,
    bgcolor: "#eef2ff",
    color: NAVY,
    border: "1px solid rgba(34, 65, 158, 0.2)",
  },
  default: {
    ...chipBaseSx,
    bgcolor: "#f1f5f9",
    color: "#475569",
    border: "1px solid rgba(71, 85, 105, 0.15)",
  },
};

const getStatusTone = (status) => {
  switch (status?.toLowerCase()) {
    case "success":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "error";
    case "refunded":
      return "info";
    default:
      return "default";
  }
};

const NameCell = ({ name, fallback = "—" }) => (
  <Box display="flex" alignItems="center" height="100%" gap={1.1}>
    <Avatar sx={avatarSx}>{(name || fallback).charAt(0).toUpperCase()}</Avatar>
    <span style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>
      {name || fallback}
    </span>
  </Box>
);

const MoneyCell = ({ value }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      height: "100%",
      pr: 2,
    }}
  >
    <span style={moneySx}>
      $
      {Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}
    </span>
  </Box>
);

const StatusChip = ({ status }) => {
  const tone = getStatusTone(status);
  const label =
    status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() ||
    "Unknown";
  return <Chip label={label} size="small" sx={statusChipSx[tone]} />;
};

const sendButtonSx = (outlined) => ({
  height: 34,
  minWidth: 104,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  px: 2,
  ...(outlined
    ? {
        color: NAVY,
        borderColor: "rgba(34, 65, 158, 0.35)",
        backgroundColor: "#ffffff",
        "&:hover": {
          borderColor: NAVY,
          backgroundColor: "#eef2ff",
        },
      }
    : {
        color: "#ffffff",
        backgroundColor: NAVY,
        borderColor: NAVY,
        "&:hover": {
          backgroundColor: "#1b3480",
          borderColor: "#1b3480",
        },
      }),
});

const MerchantInvoiceSection = () => {
  const [organizations, setOrganizations] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("tripola");
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("all");
  const [appliedPlatform, setAppliedPlatform] = useState("tripola");
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(
    ITEMS_PER_PAGE_OPTIONS[0]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let res;

      if (appliedPlatform === "tripola") {
        res = await listTripolaTransactionDataAction(
          selectedRowsPerPage,
          currentPage,
          appliedSearch || "all",
          null,
          null,
          appliedStatus !== "all" ? appliedStatus : "all",
          null,
          "all"
        );
        const payload = res?.data?.data != null || res?.data?.totalCount != null ? res.data : res;
        setOrganizations(payload?.data || []);
        setTotalRecords(payload?.totalCount || 0);
      } else if (appliedPlatform === "kyc") {
        res = await listKycTransactionDataAction(
          selectedRowsPerPage,
          currentPage,
          appliedSearch || "all",
          null,
          null,
          appliedStatus !== "all" ? appliedStatus : "all",
          null,
          "all"
        );
        const payload = res?.data?.data != null || res?.data?.totalCount != null ? res.data : res;
        setOrganizations(payload?.data || []);
        setTotalRecords(payload?.totalCount || 0);
      } else {
        res = await getOrganizations(
          currentPage,
          appliedSearch,
          selectedRowsPerPage,
          appliedStatus !== "all" ? appliedStatus : ""
        );
        setOrganizations(res.data.data);
        setTotalRecords(res.data.totalCount || 0);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to fetch ${appliedPlatform} data`,
        severity: "error",
      });
    }
    setLoading(false);
  }, [
    currentPage,
    appliedSearch,
    appliedStatus,
    appliedPlatform,
    selectedRowsPerPage,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, currentPage, selectedRowsPerPage]);

  const handleSend = async (org) => {
    setSendingId(org.id);
    try {
      await sendInvoiceAction(org.id);
      setSnackbar({
        open: true,
        message: org.isAutoInvoice
          ? "Invoice resent successfully"
          : "Invoice sent successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to send invoice",
        severity: "error",
      });
    }
    setSendingId(null);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateInvoiceStatus(id, status);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const paycentralColumns = [
    {
      field: "organizationName",
      headerName: "Organization",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <NameCell name={row.organizationName} fallback="Org" />
      ),
    },
    {
      field: "email",
      headerName: "Email Address",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <span style={{ fontSize: 15, color: "#333" }}>
          {row.email || "—"}
        </span>
      ),
    },
    {
      field: "totalFee",
      headerName: "Total Fee",
      flex: 0.9,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => <MoneyCell value={row.totalFee} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const { status, id } = row;
        const isSuccess = status === "SUCCESS";

        return (
          <Select
            size="small"
            value={status || "PENDING"}
            disabled={isSuccess}
            onChange={(e) => handleStatusChange(id, e.target.value)}
            sx={{
              minWidth: 118,
              height: 34,
              borderRadius: "10px",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "var(--app-font)",
              backgroundColor: isSuccess ? "#ecfdf5" : "#ffffff",
              color: isSuccess ? "#047857" : "#334155",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isSuccess
                  ? "rgba(4, 120, 87, 0.25)"
                  : "rgba(15, 23, 42, 0.12)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: NAVY,
                borderWidth: 1,
              },
            }}
          >
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SUCCESS">Success</MenuItem>
          </Select>
        );
      },
    },
    {
      field: "isAutoInvoice",
      headerName: "Billing Status",
      flex: 0.8,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const isAuto = row.isAutoInvoice && row.isEmailSend;
        const isSend = !row.isAutoInvoice && !row.isEmailSend;
        const label = isAuto ? "Auto Invoice" : isSend ? "Send" : "Manual";
        const tone = isAuto ? "success" : isSend ? "info" : "default";

        return <Chip label={label} size="small" sx={statusChipSx[tone]} />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 130,
      align: "right",
      headerAlign: "right",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const { isAutoInvoice, isEmailSend, id } = row;
        const showResend = isEmailSend || (isAutoInvoice && !isEmailSend);
        const isSending = sendingId === id;

        return (
          <Button
            variant={showResend ? "outlined" : "contained"}
            size="small"
            disableElevation
            startIcon={
              isSending ? (
                <CircularProgress size={14} color="inherit" />
              ) : showResend ? (
                <Refresh sx={{ fontSize: 16 }} />
              ) : (
                <Send sx={{ fontSize: 16 }} />
              )
            }
            disabled={isSending}
            onClick={(e) => {
              e.stopPropagation();
              handleSend(row);
            }}
            sx={sendButtonSx(showResend)}
          >
            {isSending ? "Sending..." : showResend ? "Resend" : "Send"}
          </Button>
        );
      },
    },
  ];

  const tripolaColumns = [
    {
      field: "tenantName",
      headerName: "Tenant Name",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <NameCell name={row.tenantName} fallback="T" />
      ),
    },
    {
      field: "tenantEmail",
      headerName: "Tenant Email",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <span style={{ fontSize: 15, color: "#333" }}>
          {row.tenantEmail || "—"}
        </span>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.9,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => <MoneyCell value={row.amount} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => <StatusChip status={row.status} />,
    },
    {
      field: "billingStatus",
      headerName: "Billing Status",
      flex: 0.8,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: () => (
        <Chip label="Auto Invoice" size="small" sx={statusChipSx.success} />
      ),
    },
  ];

  const kycColumns = [
    {
      field: "tenantName",
      headerName: "Tenant Name",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <NameCell name={row.tenantName} fallback="K" />
      ),
    },
    {
      field: "tenantEmail",
      headerName: "Tenant Email",
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ row }) => (
        <span style={{ fontSize: 15, color: "#333" }}>
          {row.tenantEmail || "—"}
        </span>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.9,
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => <MoneyCell value={row.amount} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => <StatusChip status={row.status} />,
    },
    {
      field: "billingStatus",
      headerName: "Billing Status",
      flex: 0.8,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: () => (
        <Chip label="Auto Invoice" size="small" sx={statusChipSx.success} />
      ),
    },
  ];

  const columns =
    appliedPlatform === "tripola"
      ? tripolaColumns
      : appliedPlatform === "kyc"
        ? kycColumns
        : paycentralColumns;

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <DataTable
        rows={organizations}
        columns={columns}
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        getRowId={(row) => row.id}
        onPageChange={(page) => setCurrentPage(page)}
        onPageSizeChange={(size) => {
          setSelectedRowsPerPage(size);
          setCurrentPage(1);
        }}
        filterValues={{
          search: searchValue,
          status: statusFilter,
          platform: platformFilter,
        }}
        onFilterApply={(values) => {
          setSearchValue(values.search || "");
          setStatusFilter(values.status || "all");
          setPlatformFilter(values.platform || "tripola");

          setAppliedSearch(values.search || "");
          setAppliedStatus(values.status || "all");
          setAppliedPlatform(values.platform || "tripola");

          setCurrentPage(1);
        }}
        onFilterReset={() => {
          setSearchValue("");
          setStatusFilter("all");
          setPlatformFilter("tripola");

          setAppliedSearch("");
          setAppliedStatus("all");
          setAppliedPlatform("tripola");

          setCurrentPage(1);
        }}
        filters={[
          {
            type: "search",
            placeholder: "Search organization...",
          },
          {
            type: "select",
            name: "status",
            options: [
              { label: "All Status", value: "all" },
              { label: "Success", value: "success" },
              { label: "Pending", value: "pending" },
              ...(platformFilter === "tripola" || platformFilter === "kyc"
                ? [
                    { label: "Failed", value: "failed" },
                    { label: "Refunded", value: "refunded" },
                  ]
                : []),
            ],
          },
          {
            type: "select",
            name: "platform",
            options: [
              { label: "Paycentral", value: "paycentral" },
              { label: "Tripola", value: "tripola" },
              { label: "KYC", value: "kyc" },
            ],
          },
        ]}
        onAction={(action, row) => {
          if (action === "send") handleSend(row);
        }}
        bulkActions={{
          delete: false,
        }}
        onAddClick={null}
        hasCreatePermission={false}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default MerchantInvoiceSection;
